import React, { useContext } from 'react'
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  SxProps,
  Theme,
  Typography,
  useTheme
} from '@mui/material'
import { BsExclamationLg } from 'react-icons/bs'
import LoadingButton from '../loadingButton/LoadingButton'

export type CustomAlertTypes = 'default' | 'warning'
export interface CustomAlertProps extends BoxProps {
  children?: React.ReactNode
  type?: CustomAlertTypes
  onClickIcon?: (event: React.MouseEvent) => void
}

const CustomAlertContext = React.createContext<{ type: CustomAlertTypes }>({
  type: 'default'
})

const CustomAlert = ({
  children,
  onClickIcon,
  sx,
  type = 'default',
  ...boxProps
}: CustomAlertProps) => {
  const theme = useTheme()
  const warningStyles: SxProps<Theme> = {
    padding: '2.5rem 3.5rem 2rem 2rem',
    backgroundColor: theme.palette.warning.main
  }
  return (
    <CustomAlertContext.Provider
      value={{
        type
      }}
    >
      <Box
        sx={{
          boxShadow: 3,
          padding: '1rem 2rem',
          width: 325,
          borderRadius: 3,
          position: 'relative',
          backgroundColor: 'white',
          ...(type === 'warning' && warningStyles),
          ...sx
        }}
        {...boxProps}
      >
        {children}
        {type === 'warning' && (
          <Box
            sx={{
              position: 'absolute',
              right: '0',
              top: '.5rem',
              zIndex: 100,
              userSelect: 'none'
            }}
            onClick={onClickIcon}
          >
            <BsExclamationLg
              color={theme.palette.warning.light}
              fontSize='4rem'
            />
          </Box>
        )}
      </Box>
    </CustomAlertContext.Provider>
  )
}

const CustomAlertTitle: ReactFC<{ sx?: SxProps }> = ({ children, sx }) => {
  return (
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: '1.25em',
        marginBottom: '1rem',
        ...sx
      }}
    >
      {children}
    </Typography>
  )
}

const CustomAlertBody: ReactFC<BoxProps> = ({ children, ...boxProps }) => {
  return <Box {...boxProps}>{children}</Box>
}

const CustomAlertFooter: ReactFC<BoxProps> = ({
  children,
  sx,
  ...boxProps
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '1rem',
        '& > *:not(:first-of-type)': {
          marginLeft: '1rem'
        },
        ...sx
      }}
      {...boxProps}
    >
      {children}
    </Box>
  )
}

const CustomAlertCancelButton: ReactFC<ButtonProps> = ({
  children,
  sx,
  ...buttonProps
}) => {
  const { type } = useContext(CustomAlertContext)
  const theme = useTheme()
  const warningStyles: SxProps = {
    backgroundColor: theme.palette.warning.dark
  }
  return (
    <Button
      variant='contained'
      color='inherit'
      sx={{
        ...(type === 'warning' && warningStyles)
      }}
      {...buttonProps}
    >
      {children}
    </Button>
  )
}

const CustomAlertButton: ReactFC<
  ButtonProps & {
    loading?: boolean
  }
> = ({ children, sx, ...buttonProps }) => {
  const { type } = useContext(CustomAlertContext)
  const warningStyles: SxProps = {
    backgroundColor: 'white',
    color: 'black'
  }
  return (
    <LoadingButton
      variant='contained'
      color={type !== 'default' ? 'inherit' : undefined}
      sx={{
        ...(type === 'warning' && warningStyles)
      }}
      {...buttonProps}
    >
      {children}
    </LoadingButton>
  )
}

const CustomAlertIcon: ReactFC<BoxProps> = ({ children, sx, ...boxProps }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        right: '0',
        top: '.5rem',
        zIndex: 100,
        userSelect: 'none',
        ...sx
      }}
      {...boxProps}
    >
      {children}
    </Box>
  )
}

CustomAlert.Title = CustomAlertTitle
CustomAlert.Body = CustomAlertBody
CustomAlert.Footer = CustomAlertFooter
CustomAlert.CancelButton = CustomAlertCancelButton
CustomAlert.Button = CustomAlertButton
CustomAlert.Icon = CustomAlertIcon

export default CustomAlert
